import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, memo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import {
  TimetableSnapshotResult,
  useOverwriteTimetableSnapshotMutation,
  useSaveTimetableSnapshotMutation,
} from 'src/hooks/useConvenienceDispatchMutation';
import { courseLabel } from 'src/pages/CoursesPresenter/courseMasterUtils';
import { CourseMasterError, parseCourseMasterError } from 'src/utils/courseMasterError';

type Props = {
  open: boolean;
  onClose: () => void;
  planningDate: string;
  /** 選択中のダイヤ。上書きの対象。null なら上書きできない */
  currentTimetable: { id: number; name: string } | null;
  mode: 'new' | 'overwrite';
};

/**
 * 保存を止める 422 をダイアログを閉じずに中の Alert に出す（#347 TimetableDetail と同じ文言の作り）。
 * code のある 3 つは構造を文言にし、code の無い 422（名前の重複・その日に配車が無い など）は error をそのまま。
 * 422 以外（通信断・500）は null を返し、呼び出し側が snackbar に出す。
 * truck_shared_by_trip_groups の courses[].id は、この保存で作った仮コースだとロールバックで消えているので使わない
 */
export function buildSaveAlert(err: CourseMasterError): { message: string; lines: string[] } | null {
  switch (err.code) {
    case 'first_trip_missing':
      return { message: `${err.message}（${err.trips.map((t) => `${courseLabel(t)} → ${t.firstTripCourse.code}`).join('、')}）`, lines: [] };
    case 'truck_shared_by_trip_groups':
      return { message: `${err.message}（${err.courses.map(courseLabel).join('、')}）`, lines: [] };
    case 'place_invalid':
      return {
        message: err.message,
        lines: err.places.map((p) => `${p.name ?? '（納品先不明）'}${p.address ? `（${p.address}）` : ''}: ${p.errors.join('、')}`),
      };
    case 'other':
      return err.status === 422 ? { message: err.message, lines: [] } : null;
    default:
      return null;
  }
}

const TimetableSaveDialog: FC<Props> = memo(({ open, onClose, planningDate, currentTimetable, mode }) => {
  const [name, setName] = useState('');
  const [alert, setAlert] = useState<{ message: string; lines: string[] } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // 保存の応答を待つ間に閉じたか。閉じたあとに別の保存でダイアログを開き直せるので、後から届いた応答ではダイアログを変えず、
  // 結果を snackbar で知らせるだけにする。その日のダイヤは保存 API が選ぶ（log_web_issues#403 の 2）。閉じたあとの呼び出しは
  // 保存の応答を待ってから送る（useApplyTimetableMutation）ので、保存が後から操作した呼び出しを上書きしない
  const closedWhileSavingRef = useRef(false);
  const savingRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const saveAsNew = useSaveTimetableSnapshotMutation();
  const overwrite = useOverwriteTimetableSnapshotMutation();
  const isPending = saveAsNew.isPending || overwrite.isPending;

  const handleClose = () => {
    if (savingRef.current) closedWhileSavingRef.current = true;
    ReactGA.event('close', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ 保存' });
    setName('');
    setAlert(null);
    onClose();
  };

  // 保存は止めない知らせ（truck_mismatch / course_duplicated / trip_no_taken）は保存後に 1 件ずつ知らせる
  const notifyWarnings = (result: TimetableSnapshotResult) => {
    result.warnings.forEach((w) => enqueueSnackbar(w.message, { variant: 'warning' }));
  };
  const handleError = (error: unknown) => {
    const err = parseCourseMasterError(error);
    const next = buildSaveAlert(err);
    if (next) setAlert(next);
    else enqueueSnackbar(err.message, { variant: 'error' });
  };
  const beginSaving = () => {
    setAlert(null);
    savingRef.current = true;
    closedWhileSavingRef.current = false;
  };
  /** 応答が届いたときに呼ぶ。保存中に閉じていたら true */
  const finishSaving = () => {
    savingRef.current = false;
    return closedWhileSavingRef.current;
  };

  const handleSaveAsNew = () => {
    const trimmed = name.trim();
    if (trimmed === '') return;
    ReactGA.event('apply', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ 新規保存', label: trimmed });
    beginSaving();
    saveAsNew.mutate({ planningDate, name: trimmed }, {
      onSuccess: (result) => {
        const closed = finishSaving();
        enqueueSnackbar(`ダイヤ「${result.timetable.name}」として保存しました`, { variant: 'success' });
        notifyWarnings(result);
        if (!closed) handleClose();
      },
      onError: (error) => {
        if (finishSaving()) {
          enqueueSnackbar(parseCourseMasterError(error).message, { variant: 'error' });
          return;
        }
        handleError(error);
      },
    });
  };

  const handleOverwrite = () => {
    if (currentTimetable == null) return;
    ReactGA.event('apply', { screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES, button_name: 'ダイヤ 上書き保存' });
    beginSaving();
    overwrite.mutate({ planningDate, timetableId: currentTimetable.id }, {
      onSuccess: (result) => {
        const closed = finishSaving();
        enqueueSnackbar(`ダイヤ「${result.timetable.name}」を上書き保存しました`, { variant: 'success' });
        notifyWarnings(result);
        if (!closed) handleClose();
      },
      onError: (error) => {
        if (finishSaving()) {
          enqueueSnackbar(parseCourseMasterError(error).message, { variant: 'error' });
          return;
        }
        handleError(error);
      },
    });
  };

  return (
    <Dialog
      open={open}
      // 保存中でも閉じられる（応答が返らない・オフラインのときに抜けられるように）。閉じた場合は応答で選択を変えない
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ transition: { onEntered: () => inputRef.current?.focus() } }}
    >
      <DialogTitle>{mode === 'overwrite' ? 'ダイヤを上書き保存' : 'ダイヤとして保存'}</DialogTitle>
      <DialogContent>
        {mode === 'new' ? (
          <TextField
            label="ダイヤ名"
            fullWidth
            size="small"
            required
            inputRef={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              // IME の変換確定の Enter（isComposing。Safari は keyCode 229）で送ると、変換途中の名前で保存される。
              // 名前の変更は /courses でしかできないので、確定後の Enter だけ受ける
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (e.key === 'Enter' && name.trim() && !isPending) handleSaveAsNew();
            }}
            sx={{ mt: 1 }}
            disabled={isPending}
          />
        ) : (
          <DialogContentText sx={{ mt: 1 }}>
            {`現在の配車計画（${planningDate}）で、選択中のダイヤ「${currentTimetable?.name ?? ''}」を上書きします。当日に無い地点は消えます（対象外・適用終了の地点は残ります）。よろしいですか？`}
          </DialogContentText>
        )}
        {alert && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {alert.message}
            {alert.lines.length > 0 && (
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5, maxHeight: 240, overflowY: 'auto' }}>
                {alert.lines.map((line) => <li key={line}>{line}</li>)}
              </Box>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>キャンセル</Button>
        {mode === 'overwrite' ? (
          <Button variant="contained" onClick={handleOverwrite} disabled={isPending || currentTimetable == null}>上書き保存</Button>
        ) : (
          <Button variant="contained" onClick={handleSaveAsNew} disabled={isPending || name.trim() === ''}>新規保存</Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

export default TimetableSaveDialog;
